import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/common/PageHeader";
import Spinner from "../../../components/common/Spinner";
import ErrorAlert from "../../../components/common/ErrorAlert";
import EmptyState from "../../../components/common/EmptyState";
import { fetchNotifications, markNotificationRead, Notification } from "../api";

const REMINDER_TYPE_STYLE: Record<string, string> = {
  warning: "bg-amber-100 text-amber-800",
  alert: "bg-orange-100 text-orange-800",
  critical: "bg-red-100 text-red-800",
};

const NotificationsPage: React.FC = () => {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | undefined>();
  const [loadingMore, setLoadingMore] = useState(false);

  const load = async (cursor?: string) => {
    try {
      if (!cursor) setLoading(true);
      else setLoadingMore(true);
      setError(null);

      const result = await fetchNotifications(cursor, 50);

      if (cursor) {
        setItems((prev) => [...prev, ...result.items]);
      } else {
        setItems(result.items);
      }
      setNextCursor(result.nextCursor);
    } catch {
      setError("Failed to load notifications.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleMarkRead = async (id: string) => {
    try {
      await markNotificationRead(id);
      setItems((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: "read" as const } : n)),
      );
    } catch {
      setError("Failed to mark notification as read.");
    }
  };

  const unreadCount = items.filter((n) => n.status === "unread").length;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Notifications"
        description={`Compliance reminders and alerts. ${
          unreadCount > 0 ? `${unreadCount} unread.` : "All caught up."
        }`}
      />

      {error && <ErrorAlert message={error} onRetry={() => load()} />}
      {loading && <Spinner />}

      {!loading && items.length === 0 && (
        <EmptyState
          title="No notifications"
          description="You'll see reminders here when compliance items are approaching their due date."
        />
      )}

      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((n) => (
            <div
              key={n.id}
              className={`bg-white shadow rounded-lg p-4 flex items-start justify-between gap-4 ${
                n.status === "unread" ? "border-l-4 border-primary-500" : ""
              }`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      REMINDER_TYPE_STYLE[n.reminderType] ||
                      "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {n.reminderType}
                  </span>
                  <span className="text-xs text-gray-500 capitalize">
                    {n.category.replace(/-/g, " ")}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(n.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-gray-900">{n.message}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Due: {n.nextDueDate}
                  {n.daysUntilDue !== undefined && ` (${n.daysUntilDue} days)`}
                  {n.daysOverdue !== undefined &&
                    ` (${n.daysOverdue} days overdue)`}
                </p>
              </div>
              {n.status === "unread" && (
                <button
                  onClick={() => handleMarkRead(n.id)}
                  className="flex-shrink-0 px-2 py-1 text-xs font-medium text-primary-700 hover:bg-primary-50 rounded"
                >
                  Mark read
                </button>
              )}
            </div>
          ))}

          {nextCursor && (
            <div className="text-center py-4">
              <button
                onClick={() => load(nextCursor)}
                disabled={loadingMore}
                className="px-4 py-2 text-sm font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md disabled:opacity-50"
              >
                {loadingMore ? "Loading..." : "Load more"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
